/**
 * AdminProtectedRoute — guard для всех /admin/* страниц (кроме /login).
 *
 * Поведение:
 * - Пока идёт проверка (isLoading) → показываем спиннер.
 * - Не авторизован → redirect на /admin/login?next=<currentPath>
 * - Если авторизован как CRM → redirect на /crm (CRM не имеет доступа к админке)
 * - Авторизован как admin → render children
 *
 * Поддержка returnTo:
 * - При переходе на защищённый маршрут без сессии —
 *   сохраняем ?next= чтобы после логина вернуться.
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAdminAuth } from '../../hooks/useAdminAuth';
import { useCrmAuth } from '../../hooks/useCrmAuth';

export function AdminProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated: isAdminAuthed, isLoading: isAdminLoading } = useAdminAuth();
  const { isAuthenticated: isCrmAuthed, isLoading: isCrmLoading } = useCrmAuth();
  const location = useLocation();

  const isLoading = isAdminLoading || isCrmLoading;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center">
        <div className="text-zinc-600 text-xs uppercase tracking-widest">Проверка сессии...</div>
      </div>
    );
  }

  // Если авторизован как CRM — не даём доступ к админке, редиректим в CRM
  if (isCrmAuthed) {
    return <Navigate to="/crm" replace />;
  }

  if (!isAdminAuthed) {
    // Сохраняем текущий путь в ?next= для возврата после логина
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/admin/login?next=${next}`} replace />;
  }

  return <>{children}</>;
}
